/**
 * Custom Hook: useMakeModelLookup
 * Handles 3-tier fallback for Make/Model ID lookup:
 * 1. Spreadsheet (Google Sheets CSV data)
 * 2. Provider metadata / hardcoded constants
 * 3. Provider API fallback (for models only)
 */

import { useCallback, useState } from "react";
import { showSuccessToast, showErrorToast } from "../utils/toastFactory";
import FixedAgent from "../utils/api/FixedAgent";

const normalizeVehicleName = (value) =>
  value
    ?.toString()
    .toLowerCase()
    .replace(/&/g, "and")
    .replace(/[^a-z0-9]/g, "")
    .trim() || "";

const getSpreadsheetValue = (row, fieldName) => {
  if (!row) return "";

  return (
    row[fieldName] ||
    row[Object.keys(row).find((key) => key.trim() === fieldName)] ||
    ""
  );
};

export const useMakeModelLookup = (
  makeModelMapping,
  renewData,
  ceiMake,
  niconMake,
  salamMeta,
  token,
) => {
  const [makeIdLoading, setMakeIdLoading] = useState(false);
  const [makeIdStatus, setMakeIdStatus] = useState(null);
  const [modelIdLoading, setModelIdLoading] = useState(false);
  const [modelIdStatus, setModelIdStatus] = useState(null);

  const getProviderMakes = useCallback(() => {
    if (renewData.insuranceType === "NICON") return niconMake;
    if (renewData.insuranceType === "SALAM") return salamMeta?.makes || [];
    return ceiMake;
  }, [ceiMake, niconMake, renewData.insuranceType, salamMeta?.makes]);

  /**
   * Lookup Make ID with spreadsheet first, then provider fallback.
   */
  const lookupMakeId = useCallback(
    async (makeName) => {
      if (!makeName) {
        return null;
      }

      console.log("[Make Lookup] Starting lookup for:", makeName);

      setMakeIdLoading(true);
      setMakeIdStatus(null);

      await new Promise((resolve) => setTimeout(resolve, 500));

      let foundId = null;
      let foundInSpreadsheet = false;
      const normalizedMakeName = normalizeVehicleName(makeName);

      if (makeModelMapping.data.length > 0) {
        console.log("[Make Lookup] Trying spreadsheet lookup...");

        const match = makeModelMapping.data.find(
          (row) =>
            normalizeVehicleName(getSpreadsheetValue(row, "Make Name")) ===
            normalizedMakeName,
        );
        const spreadsheetMakeId = getSpreadsheetValue(match, "Make ID");

        if (spreadsheetMakeId) {
          foundId = spreadsheetMakeId;
          foundInSpreadsheet = true;
          console.log("[Make Lookup] Found in spreadsheet:", {
            makeName,
            id: foundId,
          });
        } else {
          console.log("[Make Lookup] Not found in spreadsheet:", {
            match: match ? "Found row but missing Make ID" : "No matching row",
            availableColumns: match ? Object.keys(match) : [],
            sampleRow: match,
          });
        }
      } else {
        console.log("[Make Lookup] Spreadsheet data not loaded", {
          loading: makeModelMapping.loading,
          rowCount: makeModelMapping.data.length,
          lastFetched: makeModelMapping.lastFetched,
          error: makeModelMapping.error,
        });
      }

      if (!foundId) {
        console.log("[Make Lookup] Trying provider fallback...");
        const hardcodedMatch = getProviderMakes().find(
          (make) => normalizeVehicleName(make.name) === normalizedMakeName,
        );

        if (hardcodedMatch) {
          foundId = hardcodedMatch.id;
          console.log("[Make Lookup] Found in provider fallback:", {
            makeName,
            id: foundId,
          });
        } else {
          console.log("[Make Lookup] Not found in provider fallback either");
        }
      }

      setMakeIdLoading(false);
      console.log("[Make Lookup] Final result:", {
        foundId,
        source: foundInSpreadsheet
          ? "spreadsheet"
          : foundId
            ? "fallback"
            : "not found",
      });

      if (foundId) {
        setMakeIdStatus("success");
        setTimeout(() => setMakeIdStatus(null), 2000);
        showSuccessToast(
          foundInSpreadsheet
            ? "Make ID found"
            : "Make ID found (using fallback data)",
        );
        return foundId;
      }

      setMakeIdStatus("error");
      setTimeout(() => setMakeIdStatus(null), 2000);
      showErrorToast("Make ID not found in database");
      return null;
    },
    [
      getProviderMakes,
      makeModelMapping.data,
      makeModelMapping.error,
      makeModelMapping.lastFetched,
      makeModelMapping.loading,
    ],
  );

  /**
   * Lookup Model ID with spreadsheet first, then provider API fallback.
   */
  const lookupModelId = useCallback(
    async (modelName, makeName) => {
      if (!modelName || !makeName) {
        return null;
      }

      setModelIdLoading(true);
      setModelIdStatus(null);

      await new Promise((resolve) => setTimeout(resolve, 500));

      let foundId = null;
      let foundInSpreadsheet = false;
      const normalizedMakeName = normalizeVehicleName(makeName);
      const normalizedModelName = normalizeVehicleName(modelName);

      if (makeModelMapping.data.length > 0) {
        console.log("[Model Lookup] Trying spreadsheet lookup...");

        const match = makeModelMapping.data.find(
          (row) =>
            normalizeVehicleName(getSpreadsheetValue(row, "Make Name")) ===
              normalizedMakeName &&
            normalizeVehicleName(getSpreadsheetValue(row, "Model Name")) ===
              normalizedModelName,
        );
        const spreadsheetModelId = getSpreadsheetValue(match, "Model ID");

        if (spreadsheetModelId) {
          foundId = spreadsheetModelId;
          foundInSpreadsheet = true;
          console.log("[Model Lookup] Found in spreadsheet:", {
            makeName,
            modelName,
            id: foundId,
          });
        } else {
          console.log("[Model Lookup] Not found in spreadsheet:", {
            match: match ? "Found row but missing Model ID" : "No matching row",
            availableColumns: match ? Object.keys(match) : [],
            sampleRow: match,
          });
        }
      }

      if (!foundId) {
        console.log(
          "[Model Lookup] Spreadsheet lookup failed, trying API fallback...",
        );

        try {
          const makeObj = getProviderMakes().find(
            (make) => normalizeVehicleName(make.name) === normalizedMakeName,
          );
          const makeId = makeObj?.id;

          if (makeId) {
            const apiRes = await (renewData.insuranceType === "NICON"
              ? FixedAgent.getNiconVehicleModels({ id: makeId, token })
              : renewData.insuranceType === "SALAM"
                ? FixedAgent.getSalamVehicleModels({ id: makeId, token })
                : FixedAgent.getVehicleModelsByID({ id: makeId, token }));

            const models =
              renewData.insuranceType === "NICON"
                ? apiRes.data.data.model
                : renewData.insuranceType === "SALAM"
                  ? apiRes.data.data || []
                  : apiRes.data.modelToReturn;

            if (Array.isArray(models)) {
              const foundModel = models.find((model) => {
                const providerModelName =
                  renewData.insuranceType === "NICON" ||
                  renewData.insuranceType === "SALAM"
                    ? model.name
                    : model.modelName;

                return (
                  normalizeVehicleName(providerModelName) ===
                  normalizedModelName
                );
              });

              if (foundModel) {
                foundId =
                  renewData.insuranceType === "NICON" ||
                  renewData.insuranceType === "SALAM"
                    ? foundModel.id
                    : foundModel.vehicleModelId;
                console.log("[Model Lookup] Found in API fallback:", {
                  modelName,
                  id: foundId,
                });
              } else {
                console.log("[Model Lookup] Not found in API response");
              }
            }
          }
        } catch (err) {
          console.error("[Model Lookup] API fallback failed:", err);
        }
      }

      setModelIdLoading(false);

      if (foundId) {
        setModelIdStatus("success");
        setTimeout(() => setModelIdStatus(null), 2000);
        showSuccessToast(
          foundInSpreadsheet
            ? "Model ID found in database"
            : "Model ID found (using API fallback)",
        );
        return foundId;
      }

      setModelIdStatus("error");
      setTimeout(() => setModelIdStatus(null), 2000);
      showErrorToast("Model ID not found in database");
      return null;
    },
    [
      getProviderMakes,
      makeModelMapping.data,
      renewData.insuranceType,
      token,
    ],
  );

  return {
    lookupMakeId,
    lookupModelId,
    makeIdLoading,
    makeIdStatus,
    modelIdLoading,
    modelIdStatus,
  };
};
